import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/Header';
import { SETTINGS_UPDATED_EVENT } from '../../components/SeasonalEffects';

const THEME_OPTIONS = [
  { key: 'halloween', label: 'Thème Halloween', description: 'Chauves-souris, citrouilles, feuilles d\'automne et araignée' },
  { key: 'christmas', label: 'Thème Hiver / Noël', description: 'Neige qui tombe et guirlande lumineuse' }
];

const Dashboard = () => {
  const [stats, setStats] = useState({
    users: 0,
    movies: 0,
    transactions: 0,
    revenue: 0
  });
  const [themes, setThemes] = useState({ halloween: false, christmas: false });
  const [savingTheme, setSavingTheme] = useState(null);
  const [themeError, setThemeError] = useState('');

  useEffect(() => {
    fetchStats();
    fetchThemes();
  }, []);

  const fetchThemes = async () => {
    try {
      const res = await axios.get('/api/settings');
      if (res.data?.themes) setThemes(res.data.themes);
    } catch (error) {
      console.error('Error fetching themes:', error);
    }
  };

  const toggleTheme = async (key) => {
    const previous = themes;
    const next = { ...themes, [key]: !themes[key] };
    setThemes(next);
    setSavingTheme(key);
    setThemeError('');
    try {
      const res = await axios.put('/api/admin/settings/themes', { [key]: next[key] });
      setThemes(res.data.themes);
      window.dispatchEvent(new CustomEvent(SETTINGS_UPDATED_EVENT, { detail: { themes: res.data.themes } }));
    } catch (error) {
      console.error('Error updating theme:', error);
      setThemes(previous);
      setThemeError('Impossible de mettre à jour le thème.');
    } finally {
      setSavingTheme(null);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get('/api/admin/stats');
      const data = res.data;

      setStats({
        users: data.users,
        movies: data.movies + data.series, // Combine movies and series for "Total Films" or assume separate logic if needed. User UI says "Total Films" so combining seems appropriate or maybe "Total Content".
        transactions: data.transactions,
        revenue: data.revenue.toFixed(2)
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#141414]">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-white mb-8">Tableau de bord Admin</h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-[#1A1A1A] rounded-lg p-6 border border-[#262626]">
            <h3 className="text-gray-400 text-sm mb-2">Total Utilisateurs</h3>
            <p className="text-3xl font-bold text-white">{stats.users}</p>
          </div>
          <div className="bg-[#1A1A1A] rounded-lg p-6 border border-[#262626]">
            <h3 className="text-gray-400 text-sm mb-2">Total Films</h3>
            <p className="text-3xl font-bold text-white">{stats.movies}</p>
          </div>
          <div className="bg-[#1A1A1A] rounded-lg p-6 border border-[#262626]">
            <h3 className="text-gray-400 text-sm mb-2">Total Transactions</h3>
            <p className="text-3xl font-bold text-white">{stats.transactions}</p>
          </div>
          <div className="bg-[#1A1A1A] rounded-lg p-6 border border-[#262626]">
            <h3 className="text-gray-400 text-sm mb-2">Revenus Totaux</h3>
            <p className="text-3xl font-bold text-white">{stats.revenue}€</p>
          </div>
        </div>

        {/* Seasonal Themes */}
        <div className="bg-[#1A1A1A] rounded-lg p-6 border border-[#262626] mb-8">
          <h2 className="text-xl font-bold text-white mb-1">Thèmes saisonniers</h2>
          <p className="text-gray-400 text-sm mb-4">Effets visuels affichés sur tout le site (sauf pendant la lecture d'une vidéo).</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {THEME_OPTIONS.map((option) => (
              <label
                key={option.key}
                className="flex items-start gap-3 p-4 rounded-lg border border-[#262626] hover:bg-[#222] cursor-pointer transition"
              >
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-red-600 cursor-pointer"
                  checked={!!themes[option.key]}
                  disabled={savingTheme !== null}
                  onChange={() => toggleTheme(option.key)}
                />
                <span>
                  <span className="block text-white font-semibold">{option.label}</span>
                  <span className="block text-gray-400 text-sm">{option.description}</span>
                </span>
              </label>
            ))}
          </div>
          {themeError && <p className="text-red-500 text-sm mt-3">{themeError}</p>}
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Link
            to="/admin/movies"
            className="bg-[#1A1A1A] rounded-lg p-6 hover:bg-gray-700 transition"
          >
            <h3 className="text-xl font-bold text-white mb-2">Gérer les Films</h3>
            <p className="text-gray-400">Ajouter, modifier ou supprimer des films</p>
          </Link>
          <Link
            to="/admin/series"
            className="bg-[#1A1A1A] rounded-lg p-6 hover:bg-gray-700 transition"
          >
            <h3 className="text-xl font-bold text-white mb-2">Gérer les Séries</h3>
            <p className="text-gray-400">Ajouter, modifier ou supprimer des séries TV</p>
          </Link>
          <Link
            to="/admin/users"
            className="bg-[#1A1A1A] rounded-lg p-6 hover:bg-gray-700 transition"
          >
            <h3 className="text-xl font-bold text-white mb-2">Gérer les Utilisateurs</h3>
            <p className="text-gray-400">Voir et gérer les comptes utilisateurs</p>
          </Link>
          <Link
            to="/admin/transactions"
            className="bg-[#1A1A1A] rounded-lg p-6 hover:bg-gray-700 transition"
          >
            <h3 className="text-xl font-bold text-white mb-2">Voir les Transactions</h3>
            <p className="text-gray-400">Surveiller toutes les transactions de paiement</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
